/* eslint-disable max-len */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { TextInput } from '@manulife/mux';
import './fasatTextInput.scss';
import { currencyFormatToDecimal, currencyFormat, padWithZeroes } from '../../util';
import FasatFormErrors from '../fasatFormErrors/fasatFormErrors';
import { globalConstants } from '../../moduleConstants';

const toStringSafe = (val) => {
  if (val === null || val === undefined) {
    return '';
  }
  return typeof val === 'string' ? val : String(val);
};

const handleOnBlurFun = ({
  currencyInput, ev, locale, setValues, onBlur, durableInput, decimalPlaces, isParcent, defaultMagicNumber, inputEvent }) => {
  if (currencyInput) {
    let dataResult;
    if (ev.target.value !== '' && ev.target.value !== '-') {
      const num = ev.target.value.replace(/\s/g, '');
      const splitedValue = currencyFormatToDecimal(num, locale);
      dataResult = currencyFormat(splitedValue, locale, decimalPlaces).trimEnd();
    } else {
      dataResult = '';
    }
    setValues(dataResult);
    onBlur(dataResult);
  } else if (durableInput && ev.target.value !== '') {
    const dataResult = padWithZeroes(ev.target.value, defaultMagicNumber);
    setValues(dataResult);
    onBlur(ev, dataResult);
  // eslint-disable-next-line no-restricted-globals
  } else if (isParcent && ev.target.value && !isNaN(ev.target.value)) {
    const dataResult = parseFloat(ev.target.value).toFixed(decimalPlaces || globalConstants.numberTwo);
    setValues(dataResult);
    onBlur(ev, inputEvent);
  } else {
    const dataResult = ev.target.value;
    setValues(dataResult);
    onBlur(ev, inputEvent);
  }
};

export const validateAssign = ({ dc, inputEvent, value, beforeDc }) => {
  const regexp = /^\d*(\.\d{0,2})?$/;
  const regexp3 = /^\d*(\.\d{0,3})?$/;
  const regexp4 = /^\d*(\.\d{0,4})?$/;
  const regxBeforeThree = /^(0*(\d{1,2}(\.\d+)?)|\.\d+|100(\.0+$)?)$/;
  if (dc === globalConstants.number4 && !regexp4.test(inputEvent.target.value)) {
    // eslint-disable-next-line no-param-reassign
    inputEvent.target.value = value;
  }
  if (dc === globalConstants.number3 && !regexp3.test(inputEvent.target.value)) {
    // eslint-disable-next-line no-param-reassign
    inputEvent.target.value = value;
  }
  if (dc === globalConstants.number2 && !regexp.test(inputEvent.target.value)) {
    // eslint-disable-next-line no-param-reassign
    inputEvent.target.value = value;
  }
  if (beforeDc === globalConstants.number3 && !regxBeforeThree.test(inputEvent.target.value)
    && inputEvent.target.value.charAt(inputEvent.target.value.length - 1) !== '.') {
    // eslint-disable-next-line no-param-reassign
    inputEvent.target.value = value;
  }
};

export const numericDecimal = ({ dc, inputEvent, value }) => {
  const regex = /^(\d{0,8})(\.\d{0,2})?$/;
  const regexSixPlace = /^(\d{0,6})(\.\d{0,6})?$/;
  if (dc === globalConstants.number2 && !regex.test(inputEvent.target.value)) {
    // eslint-disable-next-line no-param-reassign
    inputEvent.target.value = value;
  }
  if (dc === globalConstants.number6 && !regexSixPlace.test(inputEvent.target.value)) {
    // eslint-disable-next-line no-param-reassign
    inputEvent.target.value = value;
  }
};

const inputFiledNumWithDecimalPipe = ({ inputEvent, value }) => {
  const numberWithPipe = /^[-.|, 0-9]+$/;
  if (!numberWithPipe.test(inputEvent.target.value)) {
    // eslint-disable-next-line no-param-reassign
    inputEvent.target.value = value;
  }
};

const numNotEmpty = ({ inputEvent, digitPlace }) => {
  const regexpOnlyNum2 = /^\d{1,2}$/;
  const regexEmpty = /^\s*$/;
  const isEmpty = regexEmpty.test(inputEvent.target.value);
  return (digitPlace === globalConstants.number2 && ((!regexpOnlyNum2.test(inputEvent.target.value)) && !isEmpty));
};
/**
   * Handles onInput event
   * @param {String} inputEvent value
   */
const handleOnInput = ({ inputEvent, dc, isParcent, value, onlyNumber, digitPlace, onInput, numDec, beforeDc, inputNum, inputNumWithPipe }) => {
  if (inputEvent.target.value) {
    if (isParcent) {
      validateAssign({ dc, inputEvent, value, beforeDc });
    }
    if (numDec) {
      numericDecimal({ dc, inputEvent, value });
    }
    if (inputNum || inputNumWithPipe) {
      inputFiledNumWithDecimalPipe({ inputEvent, value });
    }
  }
  if ((onlyNumber && digitPlace) && (numNotEmpty({ inputEvent, digitPlace }))) {
    // eslint-disable-next-line no-param-reassign
    inputEvent.target.value = value;
  }
  onInput(inputEvent);
};

function FasatTextInput(props) {
  const { value, onChange, onBlur, onBlurEnabled, onFocus, onFocusEnabled, onInput, isParcent, autoComplete, onlyNumber,
    onInputEnabled, currencyInput, durableInput, locale, errors, name, maxLength, decimalPlaces, autoHandlerCng, numDec,
    digitPlace, beforeDc, inputNum, inputNumWithPipe, hasCustomError = false, 'data-testid': dataTestId, ariaLabel, 'aria-label': ariaLabelProp, ...rest } = props;
  const accessibilityLabel = ariaLabel || ariaLabelProp;

  const [values, setValues] = useState(value);

  useEffect(() => {
    setValues(toStringSafe(value));
  }, [value]);

  /**
   * Changes the value of the current element
   * @param {String} newValue value
   * @param {String} e current event
   */
  const setChangeValue = useCallback((newValue, e) => {
    setValues(toStringSafe(newValue));
    if (autoComplete === 'on') {
      autoHandlerCng(newValue);
    } else {
      onChange(e);
    }
  }, [autoComplete, autoHandlerCng, onChange]);

  /**
   * Handles focus event
   * @param {String} inputEvent value
   * @param {Event} ev event
   */
  const handleOnFocus = useCallback((valueOrEvent, eventArg) => {
    const event = (eventArg !== undefined) ? eventArg : valueOrEvent;
    if (onFocusEnabled && event) {
      onFocus(event);
    }
  }, [onFocusEnabled, onFocus]);
  /**
   * Handles blur event
   * @param {String} inputEvent value
   * @param {Event} ev event
   */
  const defaultMagicNumber = 3;
  const handleOnBlur = useCallback((ev) => {
    const inputEvent = ev.target.value;
    if (onBlurEnabled) {
      handleOnBlurFun({
        currencyInput, ev, locale, setValues, onBlur, durableInput, isParcent, decimalPlaces, defaultMagicNumber, inputEvent });
    }
  }, [onBlurEnabled, currencyInput, locale, setValues, onBlur, durableInput, isParcent, decimalPlaces]);

  const textInputClass = 'fasatTextInput';
  const fasatTextInputErrorClass = 'withError';

  /**
   * Updates error response
   * @returns error response
   */
  const updateError = () => {
    const errorsResponse = [];
    if (errors.length > 0 && errors[0].isError) {
      errorsResponse.push({ isError: !hasCustomError });
    }
    const errorMsg = errorsResponse.map((error) => error.msg || '');
    return errorMsg;
  };
  const handleInputChange = useCallback((inputEvent) => {
    if (onInputEnabled) {
      handleOnInput({ dc: decimalPlaces, value: toStringSafe(value), inputEvent, isParcent, onlyNumber, digitPlace, onInput, numDec, beforeDc, inputNum, inputNumWithPipe });
    }
  }, [decimalPlaces, isParcent, value, onlyNumber, digitPlace, onInput, numDec, beforeDc, inputNum, inputNumWithPipe]);
  return (
    <div
      className={`${textInputClass} ${
        errors.length > 0 && errors[0].isError ? fasatTextInputErrorClass : ''
      }`}
    >
      <TextInput
        {...rest}
        value={toStringSafe(values)}
        name={name}
        onChange={setChangeValue}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onInput={handleInputChange}
        maxLength={maxLength}
        errors={updateError()}
        customStyle={{
          rootStyle: {
            width: '100%',
          },
          inputStyle: {
            height: '44px',
          },
        }}
        data-testid={dataTestId}
        aria-label={accessibilityLabel}
      />
      {updateError().length > 0 && <FasatFormErrors name={name} errors={errors} />}
    </div>
  );
}

FasatTextInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  autoHandlerCng: PropTypes.func,
  onBlur: PropTypes.func,
  onBlurEnabled: PropTypes.bool,
  onlyNumber: PropTypes.bool,
  numDec: PropTypes.bool,
  onFocus: PropTypes.func,
  onFocusEnabled: PropTypes.bool,
  onInput: PropTypes.func,
  onInputEnabled: PropTypes.bool,
  currencyInput: PropTypes.bool,
  durableInput: PropTypes.bool,
  isParcent: PropTypes.bool,
  locale: PropTypes.string,
  name: PropTypes.string,
  digitPlace: PropTypes.number,
  autoComplete: PropTypes.string,
  maxLength: PropTypes.number,
  decimalPlaces: PropTypes.number,
  beforeDc: PropTypes.number,
  hasCustomError: PropTypes.bool,
  errors: PropTypes.arrayOf(PropTypes.shape(
    {
      id: PropTypes.string,
      isError: PropTypes.bool,
      msg: PropTypes.string,
    },
  )),
  inputNum: PropTypes.bool,
  inputNumWithPipe: PropTypes.bool,
};

FasatTextInput.defaultProps = {
  value: '',
  name: '',
  maxLength: null,
  decimalPlaces: 0,
  digitPlace: 0,
  beforeDc: 0,
  onlyNumber: false,
  numDec: false,
  onBlurEnabled: false,
  onInputEnabled: false,
  isParcent: false,
  onFocusEnabled: false,
  currencyInput: false,
  durableInput: false,
  autoComplete: 'off',
  locale: 'en',
  hasCustomError: false,
  errors: [
    {
      id: '',
      isError: false,
      msg: '',
    },
  ],
  onBlur: () => undefined,
  onInput: () => undefined,
  onFocus: () => undefined,
  onChange: () => undefined,
  autoHandlerCng: () => undefined,
  inputNum: false,
  inputNumWithPipe: false,
};

export default FasatTextInput;
